<template>
  <Modal ref="modalRef" large hide-actions @shown="shownRef = true" @hidden="onHidden">
    <template #title>Add an image</template>
    <div class="image-gallery-modal">
      <p v-if="errorRef" class="error">{{ errorRef }}</p>

      <div class="upload">
        <label class="btn btn-primary file-button">
          Choose file
          <input type="file" accept="image/*" @change="onFileChange"/>
        </label>
        <div class="drop-zone" @dragover.prevent @drop.prevent="onDrop">
          Drop an image file here
        </div>
      </div>

      <template v-if="props.images.length">
        <p class="gallery-hint">Or pick a FamilySearch memory:</p>
        <!-- Only mount the tiles once the modal is shown, so their images (each a token-bearing FS
             fetch) don't all load in the background while the modal is closed. -->
        <div v-if="shownRef" class="gallery">
          <button
            v-for="image in props.images"
            :key="image.originalUrl"
            type="button"
            class="tile"
            @click="onSource(image)"
          >
            <img :src="image.displayUrl" alt="" draggable="false" loading="lazy"/>
          </button>
        </div>
      </template>
      <p v-else-if="props.loading" class="empty">Loading FamilySearch memories…</p>
      <p v-else class="empty">No FamilySearch memories for this card.</p>

      <div class="actions">
        <button type="button" class="btn btn-outline-primary" @click="close">Close</button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Modal from '@/util/Modal.vue'
import { SourceImage } from './FamilySearchGameSource'

const props = defineProps<{
  images: SourceImage[]
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', selection: { source: SourceImage } | { file: File }): void
}>()

const errorRef = ref('')
const shownRef = ref(false)

const modalRef = ref<InstanceType<typeof Modal>>()

function open() {
  errorRef.value = ''
  modalRef.value?.open()
}

function close() {
  modalRef.value?.close()
}

// Cleared when the modal hides so a stale error doesn't linger and the tiles unmount.
function onHidden() {
  shownRef.value = false
  errorRef.value = ''
}

function onSource(source: SourceImage) {
  emit('select', { source })
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    emit('select', { file })
  }
  input.value = ''
}

function onDrop(event: DragEvent) {
  const file = event.dataTransfer?.files?.[0]
  if (file && file.type.startsWith('image/')) {
    emit('select', { file })
  }
}

// Lets the parent surface an upload failure (e.g. a CORS-blocked FS fetch) inside the modal.
function showError(message: string) {
  errorRef.value = message
}

defineExpose({ open, close, showError })
</script>

<style lang="scss" scoped>
.image-gallery-modal {
  .error {
    color: #000;
    font-size: 0.875rem;
  }

  .upload {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .file-button {
    input {
      display: none;
    }
  }

  .drop-zone {
    flex: 1;
    padding: 1rem;
    text-align: center;
    color: #888;
    font-size: 0.875rem;
    border: 1px dashed #aaa;
    border-radius: 12px;
  }

  .gallery-hint {
    margin-top: 1rem;
    color: #888;
    font-size: 0.875rem;
  }

  .gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .tile {
    width: 180px;
    height: 180px;
    padding: 0;
    overflow: hidden;
    border: 1px solid #ccc;
    border-radius: 12px;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      user-select: none;
      -webkit-user-drag: none;
    }
  }

  .empty {
    margin-top: 1rem;
    color: #888;
    font-size: 0.875rem;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }
}
</style>
